import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../ui/Button';
import { motion } from 'framer-motion';
import { Star, CheckCircle, Play, Pause, Volume2, AlertCircle } from 'lucide-react';
export function HeroSection() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleLoadedData = () => {
      console.log('Audio loaded successfully');
      setAudioLoaded(true);
      setAudioError(null);
    };
    const handleError = (e: Event) => {
      console.error('Audio error:', e);
      setAudioError('No se pudo cargar el audio');
      setAudioLoaded(false);
    };
    const handleCanPlay = () => {
      console.log('Audio can play');
      setAudioLoaded(true);
    };
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    // Try to load the audio
    audio.load();
    return () => {
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, []);
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleAudio = async () => {
    if (!audioRef.current) return;
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // Reset to beginning if ended
        if (audioRef.current.ended) {
          audioRef.current.currentTime = 0;
        }
        await audioRef.current.play();
        setIsPlaying(true);
        console.log('Audio playing');
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setAudioError('Error al reproducir el audio');
      setIsPlaying(false);
    }
  };
  return <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden bg-[#F0FDFA]" data-id="element-72">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-[#CCFBF1] rounded-l-[100px] -z-10 hidden lg:block" data-id="element-73" />
      <div className="absolute top-20 left-10 w-64 h-64 bg-[#14B8A6]/10 rounded-full blur-3xl -z-10" data-id="element-74" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl" data-id="element-75">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-76">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="space-y-6 md:space-y-8" data-id="element-77">
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm border border-[#5EEAD4]/30" data-id="element-78">
              <span className="flex text-yellow-400" data-id="element-79">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} size={14} fill="currentColor" data-id="element-80" />)}
              </span>
              <span className="text-xs md:text-sm font-medium text-[#4A3B32]" data-id="element-81">
                Más de 1,200 mujeres desinflamadas
              </span>
            </div>

            <h1 onClick={scrollToOffer} className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-[#4A3B32] leading-tight cursor-pointer hover:opacity-90 transition-opacity" data-id="element-82">
              La Guía Anti-Hinchazón, <br data-id="element-83" />
              <span className="text-[#14B8A6]" data-id="element-84">
                Adiós Panza Hola Abdomen Plano
              </span>
            </h1>

            <p className="text-lg md:text-xl text-[#5D5D5D] leading-relaxed max-w-lg" data-id="element-85">
              Desinflámate en 10 minutos. Sin suplementos, sin dietas extremas y
              sin castigar tu cuerpo. La guía definitiva para mujeres ocupadas.
            </p>

            <div className="flex flex-col gap-4 pt-2" data-id="element-86">
              <Button onClick={scrollToOffer} size="lg" withIcon className="shadow-[#14B8A6]/30 shadow-xl w-full sm:w-auto" data-id="element-87">
                Descubre Cómo
              </Button>

              {/* Audio Player */}
              <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-full shadow-md border border-[#5EEAD4]/30 w-full sm:w-auto" data-id="element-88">
                <button onClick={toggleAudio} disabled={!audioLoaded && !audioError} className="w-10 h-10 flex items-center justify-center rounded-full bg-[#14B8A6] text-white hover:bg-[#0D9488] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed" aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'} data-id="element-89">
                  {audioError ? <AlertCircle size={18} data-id="element-90" /> : isPlaying ? <Pause size={18} data-id="element-91" /> : <Play size={18} className="ml-0.5" data-id="element-92" />}
                </button>
                <div className="flex items-center gap-2 flex-1" data-id="element-93">
                  <Volume2 size={16} className="text-[#14B8A6]" data-id="element-94" />
                  <span className="text-sm font-medium text-[#4A3B32]" data-id="element-95">
                    {audioError ? audioError : audioLoaded ? 'Escucha el Preview del eBook' : 'Cargando audio...'}
                  </span>
                </div>
              </div>

              <p className="text-xs text-center sm:text-left text-gray-500 flex items-center" data-id="element-96">
                <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" data-id="element-97"></span>
                Oferta disponible hoy
              </p>
            </div>

            <div className="pt-6 border-t border-[#5EEAD4]/20" data-id="element-98">
              <ul className="space-y-3" data-id="element-99">
                {['Alivio real incluso en días de oficina', 'Método 100% natural y seguro', 'Resultados visibles desde el primer día'].map((item, i) => <li key={i} className="flex items-center text-sm md:text-base text-[#4A3B32]" data-id="element-100">
                    <CheckCircle className="text-[#14B8A6] mr-3 h-5 w-5 flex-shrink-0" data-id="element-101" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Image Content */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative flex justify-center lg:justify-end" data-id="element-102">
            <div className="relative w-full max-w-md mx-auto lg:mr-0" data-id="element-103">
              {/* Image shadow/glow */}
              <div className="absolute inset-0 bg-[#14B8A6] blur-[60px] opacity-20 rounded-full transform translate-y-10" data-id="element-104" />

              <img src="https://cdn.magicpatterns.com/uploads/hZhPEEpQETkzDV2iTWL4vN/d135fca7-e6f3-4c98-aa3d-923f61a52d3b.jpg" alt="La Guía Anti-Hinchazón Libro" onClick={scrollToOffer} className="relative z-10 w-full h-auto drop-shadow-2xl rounded-lg transform hover:scale-105 transition-transform duration-500 cursor-pointer" data-id="element-105" />

              {/* Floating Badge */}
              <motion.div initial={{
              y: 20,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 1,
              duration: 0.5
            }} className="absolute -bottom-6 -left-6 md:bottom-10 md:-left-10 z-20 bg-white p-4 rounded-xl shadow-xl border border-[#5EEAD4]/20 max-w-[180px]" data-id="element-106">
                <p className="font-serif font-bold text-[#4A3B32] text-lg" data-id="element-107">
                  10 Minutos
                </p>
                <p className="text-xs text-[#5D5D5D]" data-id="element-108">
                  Es todo lo que necesitas para sentir alivio
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Audio Element */}
      <audio ref={audioRef} preload="metadata" crossOrigin="anonymous" onEnded={() => setIsPlaying(false)} onPause={() => setIsPlaying(false)} onPlay={() => setIsPlaying(true)} data-id="element-109">
        <source src="https://share.aielitesolutions.com/files/696d7a0aa354ece522c537bc/e7c73735-a3f9-4baa-8194-18c5537e6bed" type="audio/mpeg" data-id="element-110" />
        Tu navegador no soporta el elemento de audio.
      </audio>
    </section>;
}